import { headerSlotInsetOptions } from '@/components/ui/pageFrame';
import { Stack, useLocalSearchParams } from 'expo-router';
import { getTokenValue } from 'tamagui';

import { EventCartProvider } from '@/src/lib/eventCart';

/**
 * Layout for ONE event's routes (`[id]/index` detail + `[id]/confirm`).
 *
 * Its job is to mount `EventCartProvider` above both, so the event fetch and the
 * cart (picked slot + per-tier quantities) are shared: confirm draws the event
 * the detail screen already loaded instead of re-fetching it, and going back
 * from confirm re-reads the same selection instead of resetting it (see
 * `src/lib/eventCart`).
 *
 * It sits under `[id]` — not at `app/event/` — because the provider needs the
 * event id to fetch, and only a layout INSIDE the dynamic segment can read it
 * from `useLocalSearchParams`. (A layout above it would have to reach for the
 * GLOBAL params, which report whatever route is active — including, once the
 * user has left this event for some other `[id]` screen, an id that isn't an
 * event at all.) Scoping it here also matches the lifetime we want: one cart per
 * event, gone when the user leaves it.
 *
 * The nested `Stack` keeps headers ON (mirroring the root/auth defaults), so
 * each screen's own `Stack.Screen` options target THIS stack: the detail page
 * floats a transparent header over its hero, the confirm step shows a plain back
 * button.
 */
export default function EventLayout() {
  const { id } = useLocalSearchParams<{ id: string }>();

  return (
    <EventCartProvider eventId={id}>
      <Stack
        screenOptions={{
          ...headerSlotInsetOptions,
          headerShown: true,
          headerShadowVisible: false,

          gestureEnabled: true,
          contentStyle: {
            backgroundColor: getTokenValue('$color.background'),
          },
          animation: 'slide_from_right',
        }}
      />
    </EventCartProvider>
  );
}
